import type { App } from 'vue'
import { LDPlugin } from 'launchdarkly-vue-client-sdk'
import { LaunchDarkly } from './feature-flag/Provider/LaunchDarkly'
import { Simple } from './feature-flag/Provider/Simple'
import { ProviderType } from './feature-flag/ProviderType'
import type { ProviderInterface } from './feature-flag/ProviderInterface'
import { Flag } from './feature-flag/Flag'
import { Context } from './feature-flag/Context'
import type { PropertyInterface } from './feature-flag/Context/PropertyInterface'
import { PropertyId } from './feature-flag/Context/PropertyId'
import { CountryId } from './feature-flag/Context/CountryId'
import { LanguageId } from './feature-flag/Context/LanguageId'
import { TestHotelFlag } from './feature-flag/Context/TestHotelFlag'
import { UserId } from './feature-flag/Context/UserId'
import { DeviceType } from './feature-flag/Context/DeviceType'

interface FFOptions {
  clientSideID: string
  contextKind: string
  provider: string
}

let provider: ProviderInterface
let providerType: string

const FeatureFlag = {
  install: (app: App, options: FFOptions) => {
    providerType = options.provider
    switch (options.provider) {
      case ProviderType.LAUNCH_DARKLY:
        app.use(LDPlugin, {
          clientSideID: options.clientSideID,
          options: {
            plugins: [],
          },
          deferInitialization: false,
        })

        provider = new LaunchDarkly(options.contextKind)
        break
      default:
        provider = new Simple()
    }

    app.provide('featureFlagProvider', provider)
  },
}

let context = new Context([])

export const useFeatureFlags = () => {
  const startup = () => {
    if (!provider) {
      return
    }

    if (providerType === ProviderType.LAUNCH_DARKLY) {
      LaunchDarkly.startup()
    }
  }

  const isEnabled = (flag: Flag) => {
    return provider.isEnabled(flag, context)
  }

  const isDisabled = (flag: Flag) => {
    return !provider.isEnabled(flag, context)
  }

  const waitForInit = async () => {
    if (!provider) {
      return Promise.resolve()
    }
    if (context && context.properties && context.properties.length > 0) {
      provider.changeContext(context)
    }
    return provider.waitForInit()
  }

  const setContext = (newContext: Context) => {
    context = newContext
  }

  const appendContext = (newContext: Context) => {
    const merged = new Map<string, PropertyInterface>()
    context.properties.forEach((p) => merged.set(p.getKey(), p))
    newContext.properties.forEach((p) => merged.set(p.getKey(), p))
    context = new Context(Array.from(merged.values()))
  }

  return { isEnabled, isDisabled, setContext, appendContext, waitForInit, startup }
}

export {
  FeatureFlag,
  Context,
  Flag,
  PropertyId,
  LanguageId,
  CountryId,
  TestHotelFlag,
  UserId,
  DeviceType,
}
